import React from 'react';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import L from 'leaflet';
import * as turf from '@turf/turf';

import Config from '../../constants/appConfig';
import enableMarkerSlide from '../job_stats/update_marker';
import './testeMap.css';

const DEFAULT_PLANE_POSITION_INTERVAL = 30;
const DEFAULT_JOB_EVENT_INTERVAL = 5;
const DEFAULT_JOB_EVENT_BUFFER = 5;
const DEFAULT_ACTIVITY_FRAME_DURATION = 200;
const MAX_ACTIVITY_FRAME_DURATION = 2000;
const REALTIME_CATCHUP_WINDOW = 5000;
const MAX_REALTIME_SPEED = 1000;
const HIGH_CATCHUP_DELAY = 5 * 60 * 1000;
const EXTREME_CATCHUP_DELAY = 30 * 60 * 1000;
const MAX_SPEED_CHANGE_PER_FRAME = 0.25;
const PLAYBACK_SPEEDS = [1, 2, 5, 10];

const defaultSkin = {
  showBackground: '1',
  border: '#666666',
  background: '#ffffff',
  wings: '#222222',
  wingsDetail: '#555555',
  front: '#222222',
  frontDetail: '#555555',
  back: '#222222',
  hoper: '#777777',
  hoperDetail: '#333333',
  windowsColor: '#4b6f8a',
  windowsColor2: '#9ec7df',
  middle: '#222222',
  tailColor: '#222222',
  tailColor2: '#555555'
};

class TesteMap extends React.Component {
  state = {
    planes: [],
    selectedPlaneId: '',
    planePositionInterval: DEFAULT_PLANE_POSITION_INTERVAL,
    jobEventInterval: DEFAULT_JOB_EVENT_INTERVAL,
    jobEventBuffer: DEFAULT_JOB_EVENT_BUFFER,
    playbackSpeed: 1,
    autoAccelerationEnabled: true,
    heartbeatEnabled: false,
    activityEnabled: false,
    animationPaused: false,
    followPlane: false,
    loadingPlanes: true,
    planePositionStatus: 'Aguardando aeronave',
    jobEventStatus: 'Aguardando aeronave',
    currentPosition: null,
    activityItemCurrent: 0,
    activityItemTotal: 0
  };

  map = null;
  marker = null;
  applicationLayerGroup = null;
  applicationRenderer = null;
  applicationPoints = null;
  applicationPolygon = null;
  applicationSwathWidth = 0;
  planePositionTimer = null;
  jobEventTimer = null;
  animationQueue = [];
  activityHistory = [];
  activeAnimation = null;
  markerMoveHandler = null;
  markerMoveEndHandler = null;
  jobEventKeys = new Set();
  activityPlaybackRunning = false;
  playbackPosition = null;
  lastFrameTiming = null;
  adaptivePlaybackSpeed = 1;
  activityPlaybackCurrent = 0;
  activityPlaybackTotal = 0;
  requestGeneration = 0;
  jobEventCursor = 0;
  jobEventCursorByPlane = {};
  activityHistoryByPlane = {};
  latestByPlane = {};
  latestBySource = {
    plane_position: null,
    job_event: null
  };
  requestInProgress = {
    plane_position: false,
    job_event: false
  };
  isMountedFlag = false;

  componentDidMount() {
    this.isMountedFlag = true;
    if (typeof L.Marker.prototype.slideTo !== 'function') enableMarkerSlide(L);
    this.bootMap();
    this.loadPlanes();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    this.requestGeneration += 1;
    this.clearPolling();
    this.clearAnimation();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  bootMap = () => {
    this.map = L.map('teste-map-map', {
      zoomControl: false,
      attributionControl: false
    }).setView(Config.place, 14);

    L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(this.map);
    this.applicationRenderer = L.canvas({ padding: 0.5 });
    this.applicationLayerGroup = L.featureGroup().addTo(this.map);
    L.control.zoom({ position: 'bottomright' }).addTo(this.map);
    window.setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 0);
  };

  getRequestOptions = () => ({
    method: 'GET',
    headers: {
      'Authorization': localStorage.getItem('access-token-jwt')
    }
  });

  handleRequestError = (error, fallbackMessage) => {
    if (error && (error.status === 401 || error.status === 403)) {
      this.props.link('/login');
    }
    return fallbackMessage;
  };

  loadPlanes = () => {
    fetch(`${Config.server}/plane`, this.getRequestOptions())
      .then(response => {
        if (response.status !== 200) throw response;
        return response.json();
      })
      .then(result => {
        if (!this.isMountedFlag) return;
        const planes = Array.isArray(result) ? result : [];
        this.setState({
          planes,
          planePositionStatus: !this.state.heartbeatEnabled
            ? 'Desativado'
            : planes.length ? 'Buscando todas as aeronaves...' : 'Nenhuma aeronave disponível',
          jobEventStatus: !this.state.activityEnabled
            ? 'Desativado'
            : planes.length ? 'Buscando todas as aeronaves...' : 'Nenhuma aeronave disponível'
        }, () => {
          if (planes.length) this.loadInitialPositions(planes);
          else this.setState({ loadingPlanes: false });
        });
      })
      .catch(error => {
        if (!this.isMountedFlag) return;
        this.handleRequestError(error, 'Não foi possível carregar as aeronaves');
        this.setState({
          planes: [],
          loadingPlanes: false,
          planePositionStatus: 'Não foi possível carregar as aeronaves',
          jobEventStatus: 'Não foi possível carregar as aeronaves'
        });
      });
  };

  getSelectedPlane = () => this.state.planes.find(
    plane => String(this.getPlaneId(plane)) === String(this.state.selectedPlaneId)
  );

  getPlaneId = plane => {
    if (!plane) return '';
    return plane.id || plane.id_plane || plane.idPlane || plane.id_aircraft || '';
  };

  getDgpsId = plane => {
    if (!plane) return '';
    return plane.id_dgps || plane.idDgps || plane.idDGPS || '';
  };

  loadInitialPositions = planes => {
    const generation = this.requestGeneration;
    const todayStart = this.getTodayStart();
    const requests = planes.map(plane => {
      const idPlane = String(this.getPlaneId(plane));

      return Promise.all([
        this.requestPlanePosition(plane),
        this.requestJobEvent(plane, todayStart)
      ]).then(results => {
        const jobEventResult = results[1];
        this.jobEventCursorByPlane[idPlane] = jobEventResult.lastCreated || todayStart;
        const sources = {
          plane_position: results[0],
          job_event: jobEventResult.latest
        };
        this.latestByPlane[idPlane] = sources;
        return { plane, sources, jobEventPositions: jobEventResult.positions };
      });
    });

    Promise.all(requests).then(results => {
      if (!this.isMountedFlag || generation !== this.requestGeneration) return;

      let selected = null;
      results.forEach(result => {
        const resultPlaneId = String(this.getPlaneId(result.plane));
        this.activityHistoryByPlane[resultPlaneId] = result.jobEventPositions.slice();
        const latest = this.getLatestFromSources(result.sources);
        if (!latest) return;
        if (!selected || latest.timestamp > selected.latest.timestamp) {
          selected = Object.assign({}, result, { latest });
        }
      });

      if (!selected) {
        this.setState({
          loadingPlanes: false,
          planePositionStatus: this.state.heartbeatEnabled ? 'Sem posição disponível' : 'Desativado',
          jobEventStatus: this.state.activityEnabled ? 'Sem evento disponível' : 'Desativado'
        });
        return;
      }

      const selectedPlaneId = String(this.getPlaneId(selected.plane));
      this.clearAnimation();
      this.latestBySource = selected.sources;
      const shouldAnimateInitialEvents = selected.latest.source === 'job_event' && selected.jobEventPositions.length > 1;
      const initialPosition = shouldAnimateInitialEvents ? selected.jobEventPositions[0] : selected.latest;
      this.playbackPosition = initialPosition;
      this.adaptivePlaybackSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
      if (shouldAnimateInitialEvents) {
        this.activityHistory = [initialPosition];
        this.rememberJobEventPosition(initialPosition);
        this.enqueueJobEventPositions(selected.jobEventPositions.slice(1));
        this.activityPlaybackCurrent = 1;
        this.activityPlaybackTotal = selected.jobEventPositions.length;
      } else {
        this.activityHistory = selected.jobEventPositions.slice();
        this.activityHistory.forEach(this.rememberJobEventPosition);
        this.activityPlaybackCurrent = this.activityHistory.length;
        this.activityPlaybackTotal = this.activityHistory.length;
      }
      this.jobEventCursor = this.jobEventCursorByPlane[selectedPlaneId] || this.getTodayStart();

      this.setState({
        selectedPlaneId,
        loadingPlanes: false,
        currentPosition: initialPosition,
        activityItemCurrent: this.activityPlaybackCurrent,
        activityItemTotal: this.activityPlaybackTotal,
        planePositionStatus: !this.state.heartbeatEnabled
          ? 'Desativado'
          : selected.sources.plane_position ? 'Consulta inicial concluída' : 'Sem posição disponível',
        jobEventStatus: !this.state.activityEnabled
          ? 'Desativado'
          : selected.sources.job_event ? 'Consulta inicial concluída' : 'Sem evento disponível'
      }, () => {
        this.updateMarker(initialPosition);
        if (!shouldAnimateInitialEvents) this.rebuildApplicationArea(this.activityHistory);
        this.checkJobEventBuffer();
        this.restartPolling();
      });
    });
  };

  requestPlanePosition = plane => {
    if (!this.state.heartbeatEnabled) return Promise.resolve(null);
    const idDgps = this.getDgpsId(plane);
    if (!idDgps) return Promise.resolve(null);

    return fetch(`${Config.server}/plane_position/${idDgps}`, this.getRequestOptions())
      .then(response => {
        if (response.status !== 200) throw response;
        return response.json();
      })
      .then(result => this.getLatestPosition(this.flattenPositionResult(result), 'plane_position'))
      .catch(error => {
        this.handleRequestError(error, 'Falha ao atualizar');
        return null;
      });
  };

  requestJobEvent = (plane, startDate) => {
    if (!this.state.activityEnabled) return Promise.resolve({ latest: null, positions: [], lastCreated: 0 });
    const idPlane = this.getPlaneId(plane);
    if (!idPlane) return Promise.resolve({ latest: null, positions: [], lastCreated: 0 });

    return fetch(`${Config.server}/job_event/${idPlane}/${startDate}`, this.getRequestOptions())
      .then(response => {
        if (response.status !== 200) throw response;
        return response.json();
      })
      .then(result => {
        const positions = this.filterJobEventPositions(
          this.normalizeJobEventPositions(this.getJobEventActivities(result)),
          startDate
        );
        return {
          latest: positions.length ? positions[positions.length - 1] : null,
          positions,
          lastCreated: this.getLastCreatedTimestamp(positions)
        };
      })
      .catch(error => {
        this.handleRequestError(error, 'Falha ao atualizar');
        return { latest: null, positions: [], lastCreated: 0 };
      });
  };

  handlePlaneChange = event => {
    const selectedPlaneId = event.target.value;
    this.requestGeneration += 1;
    this.clearAnimation();
    this.jobEventCursor = this.jobEventCursorByPlane[selectedPlaneId] || this.getTodayStart();
    this.latestBySource = this.latestByPlane[selectedPlaneId] || { plane_position: null, job_event: null };
    this.activityHistory = (this.activityHistoryByPlane[selectedPlaneId] || []).slice();
    this.activityHistory.forEach(this.rememberJobEventPosition);
    this.requestInProgress = { plane_position: false, job_event: false };
    const cachedPosition = this.getLatestFromSources(this.latestBySource);
    this.playbackPosition = cachedPosition;
    this.adaptivePlaybackSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    this.activityPlaybackCurrent = this.activityHistory.length;
    this.activityPlaybackTotal = this.activityHistory.length;

    if (this.marker && this.map) {
      this.map.removeLayer(this.marker);
      this.marker = null;
    }

    this.setState({
      selectedPlaneId,
      animationPaused: false,
      currentPosition: cachedPosition,
      activityItemCurrent: this.activityPlaybackCurrent,
      activityItemTotal: this.activityPlaybackTotal,
      planePositionStatus: selectedPlaneId ? 'Buscando...' : 'Aguardando aeronave',
      jobEventStatus: selectedPlaneId ? 'Buscando...' : 'Aguardando aeronave'
    }, () => {
      this.restartPolling();
      if (selectedPlaneId) {
        if (cachedPosition) {
          this.updateMarker(cachedPosition);
          this.rebuildApplicationArea(this.activityHistory);
        }
        this.fetchPlanePosition();
        this.fetchJobEvent();
      }
    });
  };

  handleIntervalChange = name => event => {
    this.setState({ [name]: event.target.value });
  };

  commitInterval = name => () => {
    const fallback = name === 'planePositionInterval'
      ? DEFAULT_PLANE_POSITION_INTERVAL
      : name === 'jobEventBuffer'
        ? DEFAULT_JOB_EVENT_BUFFER
        : DEFAULT_JOB_EVENT_INTERVAL;
    const minimum = name === 'jobEventBuffer' ? 0 : 1;
    const parsed = Number(this.state[name]);
    const value = Math.max(minimum, Number.isFinite(parsed) ? parsed : fallback);
    this.setState({ [name]: value }, this.restartPolling);
  };

  handlePlaybackSpeed = playbackSpeed => {
    this.adaptivePlaybackSpeed = playbackSpeed;
    this.setState({ playbackSpeed }, this.checkJobEventBuffer);
  };

  handleAutoAccelerationChange = event => {
    const autoAccelerationEnabled = event.target.checked;
    this.adaptivePlaybackSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    this.setState({ autoAccelerationEnabled });
  };

  handleAnimationPause = () => {
    if (!this.state.animationPaused) {
      if (this.activeAnimation && this.marker) {
        const elapsed = Date.now() - this.activeAnimation.startedAt;
        this.activeAnimation.duration = Math.max(1, this.activeAnimation.duration - elapsed);
        this.activeAnimation.paused = true;
        if (this.markerMoveHandler) this.marker.off('move', this.markerMoveHandler);
        if (this.markerMoveEndHandler) this.marker.off('moveend', this.markerMoveEndHandler);
        if (typeof this.marker.slideCancel === 'function') this.marker.slideCancel();
        this.markerMoveHandler = null;
        this.markerMoveEndHandler = null;
      }
      this.setState({ animationPaused: true });
      return;
    }

    this.setState({ animationPaused: false }, () => {
      if (this.activeAnimation) {
        this.runMarkerSlide(this.activeAnimation.target, this.activeAnimation.duration);
      } else {
        this.startJobEventAnimation();
      }
      this.checkJobEventBuffer();
    });
  };

  stopMarkerSlide = () => {
    if (!this.marker) return;
    if (this.markerMoveHandler) this.marker.off('move', this.markerMoveHandler);
    if (this.markerMoveEndHandler) this.marker.off('moveend', this.markerMoveEndHandler);
    if (typeof this.marker.slideCancel === 'function') this.marker.slideCancel();
    this.markerMoveHandler = null;
    this.markerMoveEndHandler = null;
    this.activeAnimation = null;
  };

  handleShowCurrentActivity = () => {
    const latest = this.activityHistory[this.activityHistory.length - 1];
    if (!latest) return;

    this.stopMarkerSlide();
    this.activityPlaybackRunning = false;
    this.playbackPosition = latest;
    this.adaptivePlaybackSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    this.animationQueue = [];
    this.setActivityPlaybackProgress(this.activityHistory.length, this.activityHistory.length);
    this.setState({
      animationPaused: false,
      currentPosition: latest
    }, () => {
      this.updateMarker(latest);
      this.rebuildApplicationArea(this.activityHistory);
    });
  };

  handleRestartAnimation = () => {
    const first = this.activityHistory[0];
    if (!first) return;

    this.stopMarkerSlide();
    this.clearApplicationArea();
    this.playbackPosition = first;
    this.adaptivePlaybackSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    this.animationQueue = this.activityHistory.slice(1);
    this.setActivityPlaybackProgress(1, this.activityHistory.length);
    this.setState({
      animationPaused: false,
      currentPosition: first
    }, () => {
      this.updateMarker(first);
      if (this.animationQueue.length) {
        this.activityPlaybackRunning = true;
        this.startJobEventAnimation();
      }
    });
  };

  handleSourceChange = name => event => {
    const enabled = event.target.checked;
    this.setState({ [name]: enabled }, () => {
      if (name === 'heartbeatEnabled') {
        this.clearHeartbeatPolling();
        if (!enabled) this.setState({ planePositionStatus: 'Desativado' });
        if (enabled && this.state.selectedPlaneId) {
          this.setState({ planePositionStatus: 'Buscando...' });
          this.fetchPlanePosition();
          this.startHeartbeatPolling();
        }
      } else {
        this.clearActivityPolling();
        if (!enabled) this.setState({ jobEventStatus: 'Desativado' });
        if (enabled && this.state.selectedPlaneId) {
          this.setState({ jobEventStatus: 'Buscando...' });
          this.fetchJobEvent();
        }
      }

      if (enabled && !this.state.selectedPlaneId && this.state.planes.length) {
        this.setState({ loadingPlanes: true }, () => this.loadInitialPositions(this.state.planes));
      }
    });
  };

  clearHeartbeatPolling = () => {
    if (this.planePositionTimer) window.clearInterval(this.planePositionTimer);
    this.planePositionTimer = null;
  };

  clearActivityPolling = () => {
    if (this.jobEventTimer) window.clearTimeout(this.jobEventTimer);
    this.jobEventTimer = null;
  };

  clearPolling = () => {
    this.clearHeartbeatPolling();
    this.clearActivityPolling();
  };

  startHeartbeatPolling = () => {
    this.clearHeartbeatPolling();
    if (!this.state.heartbeatEnabled || !this.state.selectedPlaneId) return;
    const interval = Math.max(1, Number(this.state.planePositionInterval) || DEFAULT_PLANE_POSITION_INTERVAL) * 1000;
    this.planePositionTimer = window.setInterval(this.fetchPlanePosition, interval);
  };

  restartPolling = () => {
    this.clearPolling();
    if (!this.state.selectedPlaneId) return;
    this.startHeartbeatPolling();
    if (this.state.activityEnabled) {
      this.checkJobEventBuffer();
      this.scheduleJobEventPolling();
    }
  };

  scheduleJobEventPolling = () => {
    if (this.jobEventTimer) window.clearTimeout(this.jobEventTimer);
    this.jobEventTimer = null;
    if (!this.state.activityEnabled || !this.state.selectedPlaneId || this.requestInProgress.job_event) return;

    const delay = Math.max(1, Number(this.state.jobEventInterval) || DEFAULT_JOB_EVENT_INTERVAL) * 1000;
    this.jobEventTimer = window.setTimeout(() => {
      this.jobEventTimer = null;
      this.fetchJobEvent();
    }, delay);
  };

  fetchPlanePosition = () => {
    if (!this.state.heartbeatEnabled) return;
    const plane = this.getSelectedPlane();
    const idDgps = this.getDgpsId(plane);
    const generation = this.requestGeneration;
    if (!plane || !idDgps || this.requestInProgress.plane_position) {
      if (plane && !idDgps) this.setState({ planePositionStatus: 'Aeronave sem DGPS' });
      return;
    }

    this.requestInProgress.plane_position = true;
    fetch(`${Config.server}/plane_position/${idDgps}`, this.getRequestOptions())
      .then(response => {
        if (response.status !== 200) throw response;
        return response.json();
      })
      .then(result => {
        if (!this.isMountedFlag || generation !== this.requestGeneration) return;
        const positions = this.flattenPositionResult(result);
        const latest = this.getLatestPosition(positions, 'plane_position');
        if (latest) {
          this.latestBySource.plane_position = latest;
          this.saveSelectedPlaneSources();
          this.applyLatestPosition();
          this.setState({ planePositionStatus: `Atualizado às ${this.formatTime(new Date())}` });
        } else {
          this.setState({ planePositionStatus: 'Sem posição disponível' });
        }
      })
      .catch(error => {
        if (!this.isMountedFlag || generation !== this.requestGeneration) return;
        this.setState({
          planePositionStatus: this.handleRequestError(error, 'Falha ao atualizar')
        });
      })
      .then(() => {
        if (generation === this.requestGeneration) this.requestInProgress.plane_position = false;
      });
  };

  fetchJobEvent = () => {
    if (!this.state.activityEnabled) return;
    const plane = this.getSelectedPlane();
    const idPlane = this.getPlaneId(plane);
    const generation = this.requestGeneration;
    if (!plane || !idPlane || this.requestInProgress.job_event) return;

    if (this.jobEventTimer) window.clearTimeout(this.jobEventTimer);
    this.jobEventTimer = null;
    this.requestInProgress.job_event = true;
    const todayStart = this.getTodayStart();
    const startDate = Math.max(Number(this.jobEventCursor) || 0, todayStart);
    let receivedPositions = [];
    let addedPositions = 0;

    fetch(`${Config.server}/job_event/${idPlane}/${startDate}`, this.getRequestOptions())
      .then(response => {
        if (response.status !== 200) throw response;
        return response.json();
      })
      .then(result => {
        if (!this.isMountedFlag || generation !== this.requestGeneration) return;
        const activities = this.getJobEventActivities(result);
        receivedPositions = this.filterJobEventPositions(
          this.normalizeJobEventPositions(activities),
          startDate
        );
        const lastCreated = this.getLastCreatedTimestamp(receivedPositions);
        if (lastCreated > 0) {
          this.jobEventCursor = lastCreated;
          this.jobEventCursorByPlane[String(idPlane)] = lastCreated;
        }
        const latest = receivedPositions.length ? receivedPositions[receivedPositions.length - 1] : null;

        if (latest) {
          this.latestBySource.job_event = latest;
          this.saveSelectedPlaneSources();
          const startingNewBatch = !this.activeAnimation && !this.animationQueue.length;
          addedPositions = this.enqueueJobEventPositions(receivedPositions);
          if (addedPositions) {
            const total = this.activityHistory.length;
            const current = startingNewBatch
              ? Math.max(0, total - addedPositions)
              : this.activityPlaybackCurrent;
            this.setActivityPlaybackProgress(current, total);
            this.setState({ jobEventStatus: `${addedPositions} evento(s) recebido(s)` });
          } else {
            this.setState({ jobEventStatus: 'Sem novo evento' });
          }
        } else {
          this.setState({ jobEventStatus: 'Sem novo evento' });
        }
      })
      .catch(error => {
        if (!this.isMountedFlag || generation !== this.requestGeneration) return;
        this.setState({
          jobEventStatus: this.handleRequestError(error, 'Falha ao atualizar')
        });
      })
      .then(() => {
        if (generation !== this.requestGeneration) return;
        this.requestInProgress.job_event = false;
        if (addedPositions) {
          this.checkJobEventBuffer();
        }
        this.scheduleJobEventPolling();
      });
  };

  flattenPositionResult = result => {
    if (!result) return [];
    if (Array.isArray(result)) return result;
    if (Array.isArray(result.data)) return result.data;
    if (Array.isArray(result.positions)) return result.positions;
    return [result];
  };

  getJobEventActivities = result => {
    const activities = [];
    const append = (value, inheritedData) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(item => append(item, inheritedData));
        return;
      }

      const inherited = Object.assign({}, inheritedData || {});
      const jobGuid = value.job_guid || value.jobGuid || value.guid_job || value.guidJob || value.guid;
      const jobTitle = value.title || value.job_title || value.jobTitle || value.job_name || value.jobName || value.name;
      if (jobGuid) inherited.job_guid = jobGuid;
      if (jobTitle) inherited.title = jobTitle;

      if (Array.isArray(value.activity)) {
        value.activity.forEach(item => {
          if (item) activities.push(Object.assign({}, inherited, item));
        });
        return;
      }

      if (this.getCoordinates(value)) activities.push(Object.assign({}, inherited, value));
    };

    append(result, null);
    return activities;
  };

  getCoordinates = position => {
    if (!position) return null;
    const candidates = [
      { lat: position.lat, lng: position.lng },
      { lat: position.latitude, lng: position.longitude },
      { lat: position.y, lng: position.x }
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const lat = Number(candidates[i].lat);
      const lng = Number(candidates[i].lng);
      if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
    return null;
  };

  normalizeTimestamp = value => {
    if (value === undefined || value === null || value === '') return 0;
    const stringValue = String(value).trim();
    if (/^\d{14}$/.test(stringValue)) {
      const parsedCompact = new Date(
        Number(stringValue.slice(0, 4)),
        Number(stringValue.slice(4, 6)) - 1,
        Number(stringValue.slice(6, 8)),
        Number(stringValue.slice(8, 10)),
        Number(stringValue.slice(10, 12)),
        Number(stringValue.slice(12, 14))
      ).getTime();
      return Number.isFinite(parsedCompact) ? parsedCompact : 0;
    }

    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      if (numeric >= 100000000000000000) return numeric / 1000000;
      if (numeric >= 100000000000000) return numeric / 1000;
      return numeric < 100000000000 ? numeric * 1000 : numeric;
    }

    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  getTimestamp = position => {
    if (!position) return 0;
    const candidates = [
      position.created,
      position.created_at,
      position.createdAt,
      position.datetime,
      position.event_date,
      position.eventDate,
      position.gps_datetime,
      position.gpsDatetime,
      position.date,
      position.timestamp,
      position.time
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const timestamp = this.normalizeTimestamp(candidates[i]);
      if (timestamp > 0) return timestamp;
    }
    return 0;
  };

  normalizePosition = (position, source) => {
    const coordinates = this.getCoordinates(position);
    if (!coordinates) return null;
    const timeLapseFields = [
      position.timelapse,
      position.timeLapse,
      position.time_lapse,
      position.elapsetime,
      position.elapseTime,
      position.elapse_time,
      position.elapsedtime,
      position.elapsedTime,
      position.elapsed_time
    ];
    const rawTimeLapse = timeLapseFields.find(value => value !== undefined && value !== null && value !== '');
    const timeLapse = Number(String(rawTimeLapse).replace(',', '.'));
    const createdTimestamp = this.normalizeTimestamp(position.created);
    return Object.assign({}, position, coordinates, {
      source,
      timestamp: this.getTimestamp(position),
      createdTimestamp,
      angle: Number(position.angle !== undefined ? position.angle : position.heading) || 0,
      speed: Number(position.speed !== undefined ? position.speed : position.velocity) || 0,
      altitude: Number(position.alt !== undefined ? position.alt : position.altitude) || 0,
      flow: Number(position.lph !== undefined ? position.lph : position.flow) || 0,
      swathWidth: Number(
        position.swathwidth !== undefined
          ? position.swathwidth
          : position.swathWidth !== undefined
            ? position.swathWidth
            : position.swath_width
      ) || 0,
      timeLapse: Number.isFinite(timeLapse) ? timeLapse : null,
      spray: Number(position.active !== undefined ? position.active : position.spray) > 0
    });
  };

  normalizeJobEventPositions = positions => positions
    .map(position => this.normalizePosition(position, 'job_event'))
    .filter(Boolean)
    .sort((a, b) => a.createdTimestamp - b.createdTimestamp);

  filterJobEventPositions = (positions, startDate) => {
    const minimumTimestamp = Math.max(Number(startDate) || 0, this.getTodayStart());
    const tomorrow = new Date();
    tomorrow.setHours(24, 0, 0, 0);
    const maximumTimestamp = tomorrow.getTime();
    return positions.filter(position => (
      position.timestamp >= minimumTimestamp && position.timestamp < maximumTimestamp
    ));
  };

  getLastCreatedTimestamp = positions => positions.reduce((latestCreated, position) => {
    const created = position.createdTimestamp || this.normalizeTimestamp(position.created);
    return created > latestCreated ? created : latestCreated;
  }, 0);

  getLatestPosition = (positions, source) => positions
    .map(position => this.normalizePosition(position, source))
    .filter(Boolean)
    .pop() || null;

  getLatestFromSources = sources => {
    if (!sources) return null;
    const planePosition = sources.plane_position;
    const jobEvent = sources.job_event;
    if (!planePosition) return jobEvent || null;
    if (!jobEvent) return planePosition;
    return jobEvent.timestamp >= planePosition.timestamp ? jobEvent : planePosition;
  };

  saveSelectedPlaneSources = () => {
    if (!this.state.selectedPlaneId) return;
    this.latestByPlane[String(this.state.selectedPlaneId)] = Object.assign({}, this.latestBySource);
  };

  getJobEventKey = position => position
    ? `${position.timestamp}|${position.lat}|${position.lng}|${position.angle}`
    : '';

  rememberJobEventPosition = position => {
    const key = this.getJobEventKey(position);
    if (key) this.jobEventKeys.add(key);
  };

  enqueueJobEventPositions = positions => {
    let added = 0;
    positions.forEach(position => {
      const key = this.getJobEventKey(position);
      if (!key || this.jobEventKeys.has(key)) return;
      this.jobEventKeys.add(key);
      this.animationQueue.push(position);
      this.activityHistory.push(position);
      added += 1;
    });
    if (added && this.state.selectedPlaneId) {
      this.activityHistoryByPlane[String(this.state.selectedPlaneId)] = this.activityHistory.slice();
    }
    return added;
  };

  setActivityPlaybackProgress = (current, total) => {
    this.activityPlaybackCurrent = current;
    this.activityPlaybackTotal = total;
    if (this.isMountedFlag) {
      this.setState({
        activityItemCurrent: current,
        activityItemTotal: total
      });
    }
  };

  clearApplicationArea = () => {
    if (this.applicationLayerGroup) this.applicationLayerGroup.clearLayers();
    this.resetCurrentApplicationPath();
  };

  resetCurrentApplicationPath = () => {
    this.applicationPoints = null;
    this.applicationPolygon = null;
    this.applicationSwathWidth = 0;
  };

  renderCurrentApplicationPath = () => {
    if (!this.applicationLayerGroup || !this.applicationPoints || this.applicationPoints.length < 2) return;

    const latLngs = this.geoInvertArray(
      this.createApplicationPoly(this.applicationPoints, this.applicationSwathWidth)
    );
    if (this.applicationPolygon) {
      this.applicationPolygon.setLatLngs(latLngs);
      return;
    }

    this.applicationPolygon = L.polygon(latLngs, {
      color: '#32a852',
      fillOpacity: 0.9,
      opacity: 0,
      className: 'aplicacao',
      renderer: this.applicationRenderer
    });
    this.applicationPolygon.addTo(this.applicationLayerGroup);
  };

  drawApplicationStep = (previousPosition, position, deferRender = false) => {
    if (
      !this.applicationLayerGroup || !previousPosition || !position ||
      previousPosition.source !== 'job_event' || position.source !== 'job_event' ||
      !previousPosition.spray || !position.spray || position.swathWidth <= 0
    ) {
      this.renderCurrentApplicationPath();
      this.resetCurrentApplicationPath();
      return;
    }

    if (previousPosition.lat === position.lat && previousPosition.lng === position.lng) return;

    if (
      this.applicationPoints &&
      Math.abs(this.applicationSwathWidth - position.swathWidth) > 0.001
    ) {
      this.renderCurrentApplicationPath();
      this.resetCurrentApplicationPath();
    }

    if (this.applicationPoints) {
      this.applicationPoints.push([position.lng, position.lat]);
    } else {
      this.applicationSwathWidth = position.swathWidth;
      this.applicationPoints = [
        [previousPosition.lng, previousPosition.lat],
        [position.lng, position.lat]
      ];
    }

    if (!deferRender) this.renderCurrentApplicationPath();
  };

  drawApplicationSteps = (previousPosition, positions) => {
    let previous = previousPosition;
    positions.forEach(position => {
      this.drawApplicationStep(previous, position, true);
      previous = position;
    });
    this.renderCurrentApplicationPath();
  };

  geoInvertArray = coordinates => coordinates.map(coordinate => [coordinate[1], coordinate[0]]);

  createApplicationPoly = (points, widthLine) => {
    const width = widthLine / 2 / 1000;
    const bearings = [null, null];
    const sides = [[], []];

    for (let i = 0; i < points.length; i += 1) {
      const previous = i > 0 ? points[i - 1] : null;
      const current = points[i];
      const next = i < points.length - 1 ? points[i + 1] : null;

      if (previous) {
        const bearing = turf.bearing(previous, current);
        bearings[0] = bearing < 0 ? bearing + 360 : bearing;
      }
      if (next) {
        const bearing = turf.bearing(current, next);
        bearings[1] = bearing < 0 ? bearing + 360 : bearing;
      }

      if (previous && next) {
        let middleBearing = bearings[1];
        if (bearings[0] - bearings[1] < 10 && bearings[1] - bearings[0] < 10) {
          middleBearing = (bearings[0] - bearings[1]) / 2 + bearings[1];
        }
        sides[0].push(turf.destination(current, width, middleBearing + 90).geometry.coordinates);
        sides[1].push(turf.destination(current, width, middleBearing - 90).geometry.coordinates);
      } else if (previous) {
        sides[0].push(turf.destination(current, width, bearings[0] + 90).geometry.coordinates);
        sides[1].push(turf.destination(current, width, bearings[0] - 90).geometry.coordinates);
      } else if (next) {
        sides[0].push(turf.destination(current, width, bearings[1] + 90).geometry.coordinates);
        sides[1].push(turf.destination(current, width, bearings[1] - 90).geometry.coordinates);
      }
    }

    sides[1].reverse();
    const polygon = sides[0].concat(sides[1]);
    polygon.push(polygon[0]);
    return polygon;
  };

  rebuildApplicationArea = positions => {
    this.clearApplicationArea();
    if (positions.length > 1) this.drawApplicationSteps(positions[0], positions.slice(1));
  };

  clearAnimation = () => {
    if (this.marker) {
      if (this.markerMoveHandler) this.marker.off('move', this.markerMoveHandler);
      if (this.markerMoveEndHandler) this.marker.off('moveend', this.markerMoveEndHandler);
      if (typeof this.marker.slideCancel === 'function') this.marker.slideCancel();
    }
    this.markerMoveHandler = null;
    this.markerMoveEndHandler = null;
    this.animationQueue = [];
    this.activityHistory = [];
    this.activeAnimation = null;
    this.activityPlaybackRunning = false;
    this.playbackPosition = null;
    this.lastFrameTiming = null;
    this.adaptivePlaybackSpeed = 1;
    this.jobEventKeys = new Set();
    this.activityPlaybackCurrent = 0;
    this.activityPlaybackTotal = 0;
    this.clearApplicationArea();
  };

  getFrameTiming = (position, previousPosition) => {
    const createdDelta = position && previousPosition &&
      position.createdTimestamp > previousPosition.createdTimestamp
      ? position.createdTimestamp - previousPosition.createdTimestamp
      : 0;
    const elapsedDelta = position && previousPosition &&
      Number.isFinite(position.timeLapse) && Number.isFinite(previousPosition.timeLapse) &&
      position.timeLapse > previousPosition.timeLapse
      ? (position.timeLapse - previousPosition.timeLapse) * 1000
      : 0;
    const deltaTolerance = elapsedDelta > 0 ? Math.max(20, elapsedDelta * 0.25) : 20;
    const deltasMatch = createdDelta > 0 && elapsedDelta > 0 &&
      Math.abs(createdDelta - elapsedDelta) <= deltaTolerance;
    const useElapsedDelta = elapsedDelta > 0 && (!createdDelta || !deltasMatch);
    const sourceDuration = useElapsedDelta
      ? elapsedDelta
      : createdDelta || elapsedDelta || DEFAULT_ACTIVITY_FRAME_DURATION;
    return {
      createdDelta,
      elapsedDelta,
      source: useElapsedDelta ? 'elapsetime' : createdDelta > 0 ? 'created' : 'fallback',
      duration: Math.min(MAX_ACTIVITY_FRAME_DURATION, sourceDuration)
    };
  };

  getAnimationDuration = (position, previousPosition) => (
    this.getFrameTiming(position, previousPosition).duration
  );

  getAdaptivePlaybackTiming = position => {
    const baseSpeed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    const bufferTime = Math.max(0, Number(this.state.jobEventBuffer) || 0) * 1000;
    const lag = position && position.createdTimestamp > 0
      ? Math.max(0, Date.now() - position.createdTimestamp)
      : 0;
    const insideRealtimeBuffer = lag < bufferTime;
    if (!this.state.autoAccelerationEnabled || insideRealtimeBuffer) {
      this.adaptivePlaybackSpeed = baseSpeed;
      return {
        lag,
        baseSpeed,
        bufferTime,
        insideRealtimeBuffer,
        desiredSpeed: baseSpeed,
        effectiveSpeed: baseSpeed
      };
    }

    let catchUpSpeed;
    if (lag >= EXTREME_CATCHUP_DELAY) catchUpSpeed = 1000;
    else if (lag >= HIGH_CATCHUP_DELAY) catchUpSpeed = 100;
    else {
      catchUpSpeed = Math.min(10, Math.max(1, 1 + lag / REALTIME_CATCHUP_WINDOW));
    }
    const desiredSpeed = Math.max(baseSpeed, catchUpSpeed);
    if (this.adaptivePlaybackSpeed < baseSpeed) this.adaptivePlaybackSpeed = baseSpeed;
    if (desiredSpeed > 10 || this.adaptivePlaybackSpeed > 10) {
      this.adaptivePlaybackSpeed = desiredSpeed;
    } else {
      const speedDifference = desiredSpeed - this.adaptivePlaybackSpeed;
      const speedChange = Math.max(
        -MAX_SPEED_CHANGE_PER_FRAME,
        Math.min(MAX_SPEED_CHANGE_PER_FRAME, speedDifference)
      );
      this.adaptivePlaybackSpeed = Math.max(
        baseSpeed,
        Math.min(MAX_REALTIME_SPEED, this.adaptivePlaybackSpeed + speedChange)
      );
    }
    return {
      lag,
      baseSpeed,
      bufferTime,
      insideRealtimeBuffer,
      desiredSpeed,
      effectiveSpeed: this.adaptivePlaybackSpeed
    };
  };

  getRemainingAnimationTime = () => {
    const speed = Math.max(1, Number(this.state.playbackSpeed) || 1);
    let remaining = 0;
    if (this.activeAnimation) {
      remaining += this.activeAnimation.paused
        ? this.activeAnimation.duration
        : Math.max(0, this.activeAnimation.duration - (Date.now() - this.activeAnimation.startedAt));
    }
    let previous = this.activeAnimation
      ? this.activeAnimation.target
      : this.playbackPosition || this.state.currentPosition;
    this.animationQueue.forEach(position => {
      remaining += this.getAnimationDuration(position, previous) / speed;
      previous = position;
    });
    return remaining;
  };

  checkJobEventBuffer = () => {
    if (!this.state.activityEnabled || !this.state.selectedPlaneId) return;
    if (this.state.animationPaused) return;
    if (this.activeAnimation || !this.animationQueue.length) return;

    if (this.activityPlaybackRunning) {
      this.startJobEventAnimation();
      return;
    }

    const bufferMs = Math.max(0, Number(this.state.jobEventBuffer) || 0) * 1000;
    const bufferedTime = this.getRemainingAnimationTime();
    if (bufferedTime >= bufferMs) {
      this.activityPlaybackRunning = true;
      this.setState({ jobEventStatus: 'Reproduzindo' });
      this.startJobEventAnimation();
    } else {
      this.setState({
        jobEventStatus: `Acumulando buffer: ${(bufferedTime / 1000).toFixed(1)}s / ${(bufferMs / 1000).toFixed(1)}s`
      });
    }
  };

  startJobEventAnimation = () => {
    if (
      !this.activityPlaybackRunning || this.state.animationPaused || this.activeAnimation ||
      !this.animationQueue.length || !this.isMountedFlag
    ) return;

    const previousPosition = this.playbackPosition || this.state.currentPosition;
    const adaptiveTiming = this.getAdaptivePlaybackTiming(this.animationQueue[0]);
    const framesPerStep = this.state.autoAccelerationEnabled && adaptiveTiming.effectiveSpeed > 10
      ? Math.max(1, Math.floor(adaptiveTiming.effectiveSpeed / 10))
      : 1;
    const frameCount = Math.min(this.animationQueue.length, framesPerStep);
    const frameTargets = this.animationQueue.splice(0, frameCount);
    const target = frameTargets[frameTargets.length - 1];
    const total = Math.max(
      this.activityPlaybackTotal,
      this.activityPlaybackCurrent + this.animationQueue.length + frameCount
    );
    this.setActivityPlaybackProgress(
      Math.min(total, this.activityPlaybackCurrent + frameCount),
      total
    );
    const current = this.marker
      ? this.marker.getLatLng()
      : previousPosition
        ? { lat: previousPosition.lat, lng: previousPosition.lng }
        : null;

    if (!current || !this.marker) {
      this.drawApplicationSteps(previousPosition, frameTargets);
      this.playbackPosition = target;
      this.setState({ currentPosition: target });
      this.updateMarker(target);
      if (this.animationQueue.length) this.startJobEventAnimation();
      else {
        this.activityPlaybackRunning = false;
        this.setState({ jobEventStatus: 'Aguardando dados para buffer' });
      }
      return;
    }

    let timingPrevious = previousPosition;
    const frameTimings = frameTargets.map(position => {
      const timing = this.getFrameTiming(position, timingPrevious);
      timingPrevious = position;
      return timing;
    });
    const timingSources = Array.from(new Set(frameTimings.map(timing => timing.source)));
    const frameTiming = {
      createdDelta: frameTimings.reduce((totalDelta, timing) => totalDelta + timing.createdDelta, 0),
      elapsedDelta: frameTimings.reduce((totalDelta, timing) => totalDelta + timing.elapsedDelta, 0),
      duration: frameTimings.reduce((totalDuration, timing) => totalDuration + timing.duration, 0),
      source: timingSources.length === 1 ? timingSources[0] : 'misto'
    };
    const duration = Math.max(1, frameTiming.duration / adaptiveTiming.effectiveSpeed);
    this.lastFrameTiming = Object.assign({}, frameTiming, adaptiveTiming, {
      appliedDuration: duration,
      groupedFrames: frameCount,
      skippedFrames: frameCount - 1
    });
    this.marker.options.rotationAngle = 0;
    this.marker.setIcon(this.createPlaneIcon(this.getSelectedPlane(), target.angle));
    this.activeAnimation = {
      previousPosition,
      frameTargets,
      target,
      duration,
      startedAt: Date.now(),
      paused: false,
      lastMapPan: 0
    };

    this.runMarkerSlide(target, duration);
  };

  runMarkerSlide = (target, duration) => {
    if (!this.marker || !this.isMountedFlag) return;
    this.activeAnimation.duration = Math.max(1, duration);
    this.activeAnimation.startedAt = Date.now();
    this.activeAnimation.paused = false;

    if (typeof this.marker.slideCancel === 'function') {
      this.marker.off('move', this.marker.slideCancel, this.marker);
    }

    this.markerMoveHandler = event => {
      const animation = this.activeAnimation;
      if (!animation || !this.marker || !this.isMountedFlag) return;
      const now = Date.now();
      if (this.state.followPlane && this.map && now - animation.lastMapPan >= 16) {
        animation.lastMapPan = now;
        this.map.panTo(event.latlng, { animate: false });
      }
    };

    this.markerMoveEndHandler = () => {
      if (!this.activeAnimation || !this.marker) return;
      const completedAnimation = this.activeAnimation;
      this.marker.off('move', this.markerMoveHandler);
      this.markerMoveHandler = null;
      this.markerMoveEndHandler = null;
      this.activeAnimation = null;
      this.drawApplicationSteps(completedAnimation.previousPosition, completedAnimation.frameTargets);
      this.playbackPosition = target;
      this.setState({ currentPosition: target });
      if (this.animationQueue.length) {
        this.startJobEventAnimation();
      } else {
        this.activityPlaybackRunning = false;
        this.setState({ jobEventStatus: 'Aguardando dados para buffer' });
      }
    };

    this.marker.on('move', this.markerMoveHandler);
    this.marker.once('moveend', this.markerMoveEndHandler);
    this.marker.slideTo([target.lat, target.lng], {
      duration: this.activeAnimation.duration,
      keepAtCenter: false
    });
  };

  applyLatestPosition = () => {
    const latest = this.getLatestFromSources(this.latestBySource);
    if (!latest) return;
    if (this.activeAnimation || this.activityPlaybackRunning || this.animationQueue.length) return;

    this.playbackPosition = latest;
    this.setState({ currentPosition: latest }, () => this.updateMarker(latest));
  };

  updateMarker = position => {
    if (!this.map) return;
    const plane = this.getSelectedPlane();
    const coordinates = [position.lat, position.lng];
    const icon = this.createPlaneIcon(plane, position.angle);

    if (!this.marker) {
      this.marker = L.marker(coordinates, { icon, rotationAngle: 0 }).addTo(this.map);
      if (typeof this.marker.slideCancel === 'function') {
        this.marker.off('move', this.marker.slideCancel, this.marker);
      }
      this.marker.bindTooltip(this.createPlaneLabel(plane), {
        direction: 'top',
        offset: [0, -24]
      });
      this.map.setView(coordinates, Math.max(this.map.getZoom(), 14));
    } else {
      this.marker.options.rotationAngle = 0;
      this.marker.setIcon(icon);
      this.marker.setLatLng(coordinates);
      this.marker.setTooltipContent(this.createPlaneLabel(plane));
      if (this.state.followPlane) this.map.panTo(coordinates, { animate: true });
    }
  };

  createPlaneLabel = plane => {
    const label = document.createElement('span');
    label.textContent = plane ? (plane.name || plane.prefix || `Aeronave ${this.getPlaneId(plane)}`) : 'Aeronave';
    return label;
  };

  parseSkin = plane => {
    if (!plane || !plane.svg) return defaultSkin;
    try {
      const parsed = typeof plane.svg === 'string' ? JSON.parse(plane.svg) : plane.svg;
      return Object.assign({}, defaultSkin, parsed || {});
    } catch (error) {
      return defaultSkin;
    }
  };

  safeColor = (value, fallback) => {
    const color = String(value || '');
    if (/^#[0-9a-f]{3,8}$/i.test(color) || /^rgba?\([\d\s.,%]+\)$/i.test(color) || /^[a-z]{3,20}$/i.test(color)) return color;
    return fallback;
  };

  createPlaneIcon = (plane, angle) => {
    const skin = this.parseSkin(plane);
    const color = (name, fallback) => this.safeColor(skin[name], fallback);
    const hideBackground = String(skin.showBackground) === '2' ? 'none' : 'inline';
    const rotation = Number(angle) || 0;
    const html = `
      <svg class="teste-map-aircraft" viewBox="-200 -200 2560 1820" width="128" height="61" aria-hidden="true" style="transform:rotate(${rotation}deg)">
        <path display="${hideBackground}" fill="${color('background', '#ffffff')}" stroke="${color('border', '#666666')}" stroke-width="24" d="M921.79 423.76l-769.47 0c-89.99-1.59-147.49 58.49-142.52 151.01l16.37 217.02 903.71 0 52.81 366.09-244.02 0c-50.85.91-97.98 36.57-87.52 97.53l29.27 140.68 203.46-.16 0 28.11 145.18 0 54.71 82.61 55.16-82.61 144.73 0-.01-28.11 203.47.16 29.26-140.68c10.47-60.96-36.66-96.62-87.52-97.53l-244.01 0 52.8-366.09 903.72 0 16.37-217.02c4.96-92.52-52.53-152.6-142.52-151.01l-769.47 0c-20.22-203.91-74.48-326.49-161.98-412.03-87.51 85.54-141.77 208.12-161.98 412.03z"/>
        <path fill="${color('wings', '#222222')}" d="M79.81 737.13l1003.97 0 1003.96 0c2.26-65.52 7.02-131.94 6.79-196.55-.12-35.36-28.93-64.29-64.28-64.29l-946.47 0-946.47 0c-35.36 0-64.16 28.93-64.29 64.29-.22 64.61 4.53 131.03 6.79 196.55z"/>
        <path fill="${color('front', '#222222')}" d="M1083.78 85.52l35.03 74.63c25.94 65.15 63.11 243.78 62.78 289.46l0 210.12c.16 17.48-1.86 45.3-5.47 80.6l-92.34 142.56-91.43-133.72c-4.02-38.77-6.56-68.82-6.39-89.44l0-210.12c-.33-45.68 36.84-224.31 62.77-289.46l35.05-74.63z"/>
        <path fill="${color('back', '#222222')}" d="M1083.78 1425.95c-4.34-33.99-9.28-70.8-14.57-109.29l-16.26 0 0 30.41-333.36 0c-6.49-27.03-12.53-94.83-12.68-128.66-.04-7.19 5.88-13.08 13.07-13.08l333.57 0c-23.65-167.24-49.58-345.3-61.3-456.16l91.43 133.72 91.49-134.3c-11.7 110.83-37.68 289.21-61.37 456.74l333.57 0c7.19 0 13.1 5.89 13.08 13.08-.16 33.83-6.19 101.63-12.68 128.66l-333.36 0 0-30.41-16.26 0c-5.29 38.49-10.22 75.3-14.56 109.29z"/>
        <path fill="${color('hoper', '#777777')}" stroke="${color('hoperDetail', '#333333')}" stroke-width="12" d="M1011.56 409.09c46-9 99-9 144.42 0l6.12 44.24 0 192.25-5.57 0c-4.08-15.55-16.12-25.51-33.99-29.18-25.83-2.95-51.32-3.09-77.58 0-18.9 3.82-30.38 13.63-33.95 29.18l-5.56 0 0-192.25 6.11-44.24z"/>
        <polygon fill="${color('middle', '#222222')}" points="1083.78,914.26 994.37,767.77 988.48,709.01 1083.78,859.28 1179.09,708.74 1173.23,767.35"/>
        <path fill="${color('windowsColor', '#4b6f8a')}" stroke="${color('windowsColor2', '#9ec7df')}" stroke-width="10" d="M1045 623l77 0 24 42-31 39-62 0-31-39c2-22 10-35 23-42z"/>
        <rect fill="${color('wingsDetail', '#555555')}" x="940" y="476" width="45" height="212"/>
        <rect fill="${color('wingsDetail', '#555555')}" x="1182" y="476" width="45" height="212"/>
        <path fill="${color('tailColor', '#222222')}" stroke="${color('tailColor2', '#555555')}" stroke-width="10" d="M1078 1350l5.78 76 5.72-76 0-164c-4-5-8-5-11.5 0z"/>
      </svg>`;

    return L.divIcon({
      className: 'teste-map-plane-icon',
      html,
      iconSize: [128, 61],
      iconAnchor: [64, 30],
      popupAnchor: [0, -30]
    });
  };

  getTodayStart = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  formatTime = date => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  formatPositionDate = timestamp => timestamp > 0 ? new Date(timestamp).toLocaleString() : '-';

  renderInfo = (label, value) => (
    <div className="teste-map-info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );

  render() {
    const position = this.state.currentPosition;
    const selectedPlane = this.getSelectedPlane();
    const positionType = (this.activeAnimation || (position && position.source === 'job_event'))
      ? 'activity'
      : 'heartbeat';
    const activityProgress = positionType === 'activity' && this.state.activityItemTotal > 0
      ? ` ${this.state.activityItemCurrent}/${this.state.activityItemTotal}`
      : '';

    return (
      <div className="teste-map-page">
        <aside className="teste-map-panel divContent">
          <h1>Teste Map</h1>


          <div className="teste-map-source-request-row">
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={this.state.heartbeatEnabled}
                  onChange={this.handleSourceChange('heartbeatEnabled')}
                />
              }
              label="Heartbeat"
            />
            <TextField
              label="Intervalo (s)"
              type="number"
              value={this.state.planePositionInterval}
              onChange={this.handleIntervalChange('planePositionInterval')}
              onBlur={this.commitInterval('planePositionInterval')}
              inputProps={{ min: 1 }}
            />
          </div>

          <div className="teste-map-source-request-row">
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={this.state.activityEnabled}
                  onChange={this.handleSourceChange('activityEnabled')}
                />
              }
              label="Activity"
            />
            <TextField
              label="Intervalo (s)"
              type="number"
              value={this.state.jobEventInterval}
              onChange={this.handleIntervalChange('jobEventInterval')}
              onBlur={this.commitInterval('jobEventInterval')}
              inputProps={{ min: 1 }}
            />
          </div>


          <div className="teste-map-playback-controls">
            <TextField
              label="buffer (s)"
              type="number"
              value={this.state.jobEventBuffer}
              onChange={this.handleIntervalChange('jobEventBuffer')}
              onBlur={this.commitInterval('jobEventBuffer')}
              inputProps={{ min: 0, step: 1 }}
            />
            <div className="teste-map-speed-control">
              <span>Acelerador</span>
              <div className="teste-map-speed-buttons">
                {PLAYBACK_SPEEDS.map(speed => (
                  <Button
                    key={speed}
                    size="small"
                    color="primary"
                    variant={this.state.playbackSpeed === speed ? 'contained' : 'outlined'}
                    onClick={() => this.handlePlaybackSpeed(speed)}
                  >
                    {speed}x
                  </Button>
                ))}
              </div>
            </div>
            <FormControlLabel
              className="teste-map-auto-acceleration"
              control={
                <Switch
                  color="primary"
                  checked={this.state.autoAccelerationEnabled}
                  onChange={this.handleAutoAccelerationChange}
                />
              }
              label="Aceleração automática"
            />
            <div className="teste-map-animation-buttons">
              <Button
                color="primary"
                variant="contained"
                onClick={this.handleAnimationPause}
                disabled={!this.state.activityEnabled}
              >
                {this.state.animationPaused ? 'Continuar' : 'Pausar'}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={this.handleShowCurrentActivity}
                disabled={!this.state.activityItemTotal}
              >
                Atual
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={this.handleRestartAnimation}
                disabled={!this.state.activityItemTotal}
              >
                Reiniciar
              </Button>
            </div>
          </div>

          <FormControl fullWidth margin="normal">
            <InputLabel id="teste-map-plane-label">Aeronave</InputLabel>
            <Select
              labelId="teste-map-plane-label"
              value={this.state.selectedPlaneId}
              onChange={this.handlePlaneChange}
              disabled={this.state.loadingPlanes}
            >
              <MenuItem value=""><em>Selecione</em></MenuItem>
              {this.state.planes.map(plane => (
                <MenuItem key={this.getPlaneId(plane)} value={String(this.getPlaneId(plane))}>
                  {plane.name || plane.prefix || `Aeronave ${this.getPlaneId(plane)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>





          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={this.state.followPlane}
                onChange={event => this.setState({ followPlane: event.target.checked })}
              />
            }
            label="Seguir aeronave no mapa"
          />

          <div className="teste-map-source-status">
            <div><span>plane_position</span><small>{this.state.planePositionStatus}</small></div>
            <div><span>job_event</span><small>{this.state.jobEventStatus}</small></div>
          </div>

          <h2>{`Posição ${positionType}${activityProgress}`}</h2>
          {position ? (
            <div className="teste-map-info">
              {this.renderInfo('Aeronave', selectedPlane ? (selectedPlane.name || selectedPlane.prefix || this.getPlaneId(selectedPlane)) : '-')}
              {position.title && this.renderInfo('Trabalho', position.title)}
              {this.renderInfo('Fonte', position.source)}
              {this.renderInfo('Data/hora created', this.formatPositionDate(position.timestamp))}
              {this.renderInfo('Data/hora atual', this.formatPositionDate(new Date()))}
              {this.renderInfo('Latitude', position.lat.toFixed(6))}
              {this.renderInfo('Longitude', position.lng.toFixed(6))}
              {this.renderInfo('Rumo', `${position.angle.toFixed(1)}°`)}
              {this.renderInfo('Velocidade', `${position.speed.toFixed(1)} km/h`)}
              {this.renderInfo('Altitude', `${position.altitude.toFixed(1)} m`)}
              {position.source === 'job_event' && this.renderInfo('Aplicação', position.spray ? 'Ligada' : 'Desligada')}
              {position.source === 'job_event' && this.renderInfo('Fluxo', `${position.flow.toFixed(1)} L/h`)}
              {position.source === 'job_event' && this.renderInfo('Faixa', `${position.swathwidth.toFixed(1)} m`)}
              {position.source === 'job_event' && this.renderInfo('elapsetime', `${position.elapsetime.toFixed(1)} s`)}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Δ created',
                `${this.lastFrameTiming.createdDelta.toFixed(0)} ms`
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Δ elapsetime',
                `${this.lastFrameTiming.elapsedDelta.toFixed(0)} ms`
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Tempo do frame',
                `${this.lastFrameTiming.appliedDuration.toFixed(0)} ms`
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Tempo baseado em',
                this.lastFrameTiming.source
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Atraso do created',
                `${(this.lastFrameTiming.lag / 1000).toFixed(2)} s`
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Velocidade efetiva',
                `${this.lastFrameTiming.effectiveSpeed.toFixed(2)}x`
              )}
              {position.source === 'job_event' && this.lastFrameTiming && this.renderInfo(
                'Frames agrupados',
                this.lastFrameTiming.groupedFrames
              )}
            </div>
          ) : (
            <p className="teste-map-empty">Selecione uma aeronave para iniciar o acompanhamento.</p>
          )}
        </aside>

        <main className="teste-map-map-card divContent">
          <div id="teste-map-map" />
        </main>
      </div>
    );
  }
}

export default TesteMap;
